/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useEffect } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { FloatButton } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, changeAppState, getLng, getDict, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tbtype = {
  type:"default" | "primary" | undefined,
  shape:"circle" | "square",
  trigger:"click" | "hover",
  placement?:"top" | "right" | "bottom" | "left" | undefined
};

const { rz } = window;

const RZfloatButton:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZfloatButton.displayName = "FloatButton";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {icon, iconClass, iconColor, bgColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    // Debug:
    if (!~userClss.indexOf("floatbutton")) { userClss.push("floatbutton") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    if (bgColor) { est.backgroundColor = bgColor }
    if (!est.zIndex) { est.zIndex = 500 }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions, bgColor]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons key={key + "-icon"} icopath={icon} attrs={{className: iconClass, color: iconColor, bgcolor: bgColor}} />) }
    return ico;
  }, [key, icon, iconClass, iconColor, bgColor]);

  const btype = useMemo<Tbtype>(() => {
    const typ:Tbtype = {type: "default", shape: "circle", trigger: "click"};
    (type || "").toLowerCase().replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      if (~["default", "primary"].indexOf(tt)) { typ.type = tt }
      else if (~["hover", "click"].indexOf(tt)) { typ.trigger = tt }
      else if (~["circle", "square"].indexOf(tt)) { typ.shape = tt }
      else if (~["expandtop", "expandright", "expandbottom", "expandleft"].indexOf(tt)) { typ.placement = tt.replace("expand").toLowerCase() }
    });

    if (elState.attributes?.trigger) { typ.trigger = elState.attributes.trigger }
    if (elState.attributes?.shape) { typ.shape = elState.attributes.shape }
    if (elState.attributes?.placement) { typ.placement = elState.attributes.placement }

    return typ;
  }, [type, elState.attributes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (fnc.config?.isDev) console.log({action: "<FloatButton key='" + key + "'> -> onClick()"});
    if (rootNamespace && (attributes.onClick || attributes.onclick) && rootNamespace[attributes.onClick || attributes.onclick]) {
      const clientFNC = rootNamespace[attributes.onClick || attributes.onclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, safe]);

  // @ts-ignore
  if (children && children.length) {
    return (
      <FloatButton.Group
        key={key} style={styl} className={clsu.join(" ")}
        shape={btype.shape}
        type={btype.type || "default"}
        trigger={btype.trigger}
        placement={btype.placement || "top"}
        icon={rico}
        onClick={onClick}
        uk-tooltip={tooltip}
        description={title}
      >
        {children}
      </FloatButton.Group>
    );
  }

  return (
    <FloatButton
      key={key} style={styl} className={clsu.join(" ")}
      shape={btype.shape}
      type={btype.type || "default"}
      icon={rico}
      onClick={onClick}
      uk-tooltip={tooltip}
      description={title}
    />
  );
}

export default RZfloatButton;
/* eslint-enable @typescript-eslint/no-unused-vars */
